import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { reserveGoogle } from '../utils/googleQuota';
import { Box, CircularProgress, Typography, Paper, Button, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapIcon from '@mui/icons-material/Map';
import SatelliteIcon from '@mui/icons-material/Satellite';

// Helper function to format component caller info
function getComponentCallerInfo() {
  const stack = new Error().stack.split('\n');
  if (stack.length > 2) {
    const importerLine = stack.find(line => 
      line.includes('webpack_require') || 
      line.includes('at Module.') || 
      line.includes('at eval ('));
    
    if (importerLine) {
      // Extract component name and simplify the path
      const componentMatch = importerLine.match(/\/([\w]+\.js)/);
      const lineMatch = importerLine.match(/:(\d+):/);
      
      if (componentMatch && lineMatch) {
        return `Imported by ${componentMatch[1]}:${lineMatch[1]}`;
      }
    }
    
    // If no good match found, return a simplified version of the first meaningful stack line
    const simpleLine = stack[2].trim()
      .replace(/at Object.eval \(eval at (.*)\)/, 'React render process')
      .replace(/at Object.<anonymous> \((.*?)\)/, 'Module $1');
    
    return simpleLine;
  }
  return 'React component tree';
}

// Simple API key validation
function validateAndLogApiKey() {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  
  if (!googleMapsApiKey) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Google Maps API key is missing from environment variables');
    }
    return { 
      isValid: false, 
      error: 'API key missing from environment variables',
      key: null 
    };
  }
  
  // Validation checks
  const checks = {
    hasCorrectPrefix: googleMapsApiKey.startsWith('AIza'),
    hasValidLength: googleMapsApiKey.length >= 30 && googleMapsApiKey.length <= 50,
    hasValidChars: /^[a-zA-Z0-9_-]+$/.test(googleMapsApiKey),
    isNotEmpty: googleMapsApiKey.trim().length > 0
  };
  
  const isValid = Object.values(checks).every(check => check);
  
  if (!isValid && process.env.NODE_ENV === 'development') {
    console.error('Google Maps API key format validation failed');
  }
  return { 
    isValid, 
    error: isValid ? null : 'API key format validation failed',
    key: googleMapsApiKey,
    checks 
  };
}

// Log comprehensive initialization info
const keyValidation = validateAndLogApiKey();

// Google Maps loader configuration
const createGoogleMapsLoader = () => {
  if (!keyValidation.isValid) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Cannot create Google Maps loader - API key validation failed');
    }
    return null;
  }
  
  const config = {
    apiKey: keyValidation.key,
    version: "weekly",
    libraries: ["geometry", "places"]
  };
  
  try {
    const loader = new Loader(config);
    return loader;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to create Google Maps Loader:', error);
    }
    return null;
  }
};

const loader = createGoogleMapsLoader();
const MAP_DEBUG = process.env.REACT_APP_MAP_DEBUG === 'true';

// Validate API key on load
const checkGoogleMapsKey = async () => {
  if (!loader) {
    if (process.env.NODE_ENV === 'development') {
      console.error('No Google Maps loader available');
    }
    return false;
  }
  
  try {
    await loader.load();
    return window.google && window.google.maps;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Google Maps API key validation failed:', error.message);
    }
    return false;
  }
};

// Define available map types
const MAP_TYPES = {
  roadmap: 'roadmap',     // Normal street map
  satellite: 'satellite', // Satellite imagery
  hybrid: 'hybrid',       // Satellite with street names
  terrain: 'terrain'      // Physical relief map
};

function MapComponent({ customers, onMarkerClick }) {
  MAP_DEBUG && console.log('[MapComponent] Received customers:', customers);
  MAP_DEBUG && console.log('[MapComponent] Number of customers:', customers.length);
  
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mapType, setMapType] = useState('roadmap');
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  
  // Enhanced validation function with detailed logging
  const validateApiKey = async () => {
    MAP_DEBUG && console.group('🔐 [MapComponent] API Key Validation Process');
    
    if (!keyValidation.isValid) {
      const errorMsg = `Google Maps API key validation failed: ${keyValidation.error}`;
      console.error('❌', errorMsg);
      setError(errorMsg);
      setLoading(false);
      MAP_DEBUG && console.groupEnd();
      return false;
    }
    
    MAP_DEBUG && console.log('✅ Initial API key format validation passed');
    
    // Run live validation
    const isValid = await checkGoogleMapsKey();
    if (!isValid) {
      const errorMsg = 'Google Maps API key live validation failed. Please check your API key and ensure Maps JavaScript API is enabled in Google Cloud Console.';
      console.error('❌', errorMsg);
      setError(errorMsg);
      setLoading(false);
      MAP_DEBUG && console.groupEnd();
      return false;
    }
    
    MAP_DEBUG && console.log('🎉 API key validation completed successfully');
    MAP_DEBUG && console.groupEnd();
    return true;
  };
  
  // Function to initialize the map with enhanced logging
  const initializeMap = async () => {
    MAP_DEBUG && console.group('🗺️ [MapComponent] Map Initialization Process');
    
    if (!await validateApiKey()) {
      console.error('❌ Map initialization aborted - API key validation failed');
      MAP_DEBUG && console.groupEnd();
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      MAP_DEBUG && console.log('🔧 Creating Google Maps instance...');
      MAP_DEBUG && console.log('📍 Map Configuration:');
      MAP_DEBUG && console.log(`  • Container: ${mapContainer.current ? 'Available' : 'Missing'}`);
      MAP_DEBUG && console.log(`  • Center: { lat: 39.8283, lng: -98.5795 } (Center of USA)`);
      MAP_DEBUG && console.log(`  • Zoom: 4`);
      MAP_DEBUG && console.log(`  • Map Type: ${mapType}`);
      
      const allowed = await reserveGoogle('mapsJavascript');
      if (!allowed) {
        setLoading(false);
        MAP_DEBUG && console.groupEnd();
        return;
      }

      // Load Google Maps API if not already loaded
      const google = await loader.load();
      MAP_DEBUG && console.log('✅ Google Maps JavaScript API loaded');
      
      // Create the map
      const mapConfig = {
        center: { lat: 39.8283, lng: -98.5795 }, // Center of USA
        zoom: 4,
        mapTypeId: mapType,
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true,
        gestureHandling: 'cooperative'
      };
      
      map.current = new google.maps.Map(mapContainer.current, mapConfig);
      MAP_DEBUG && console.log('✅ Google Maps instance created successfully');
      MAP_DEBUG && console.log('📊 Map Configuration Applied:', mapConfig);

      // Create a single InfoWindow instance to reuse
      infoWindowRef.current = new google.maps.InfoWindow();
      MAP_DEBUG && console.log('✅ InfoWindow created');
      
      // Add map event listeners for debugging
      map.current.addListener('idle', () => {
        MAP_DEBUG && console.log('🗺️ Map is idle (fully loaded)');
      });
      
      map.current.addListener('tilesloaded', () => {
        MAP_DEBUG && console.log('🗺️ Map tiles loaded');
      });
      
      MAP_DEBUG && console.log('🎉 Google Maps initialization completed successfully');
      setLoading(false);
    } catch (err) {
      console.error('💥 Error initializing Google Maps:', err);
      console.error('📊 Initialization Error Details:');
      console.error(`  • Error Type: ${err.constructor.name}`);
      console.error(`  • Error Message: ${err.message}`);
      console.error(`  • Stack Trace:`, err.stack);
      
      // Enhanced error analysis
      const errorMessage = err.message.toLowerCase();
      let userFriendlyError = 'Failed to initialize Google Maps. ';
      
      if (errorMessage.includes('api key')) {
        userFriendlyError += 'API key issue detected. Please verify your Google Maps API key.';
      } else if (errorMessage.includes('quota') || errorMessage.includes('billing')) {
        userFriendlyError += 'Billing or quota issue. Please check your Google Cloud Console.';
      } else if (errorMessage.includes('container')) {
        userFriendlyError += 'Map container issue. Please try refreshing the page.';
      } else {
        userFriendlyError += 'Please try again later or check your internet connection.';
      }
      
      setError(userFriendlyError);
      setLoading(false);
    } finally {
      MAP_DEBUG && console.groupEnd();
    }
  };
  
  // Initialize map on component mount
  useEffect(() => {
    initializeMap();
    
    // Cleanup function
    return () => {
      clearMarkers();
    };
  }, []);
  
  // When map type changes, update the map
  useEffect(() => {
    if (map.current) {
      map.current.setMapTypeId(mapType);
    }
  }, [mapType]);
  
  // Check if coordinates are valid
  const isValidCoordinate = (coords) => {
    if (!coords || !Array.isArray(coords) || coords.length !== 2) return false;
    const [lng, lat] = coords;
    return !isNaN(lng) && !isNaN(lat) 
      && lng >= -180 && lng <= 180 
      && lat >= -90 && lat <= 90;
  }
  
  // Ensure coordinates are in the correct format [longitude, latitude]
  const ensureCorrectFormat = (coords) => {
    // If already in the right format, return as is
    if (Array.isArray(coords) && coords.length === 2) {
      // Sometimes data might come with latitude first, so we check and fix if needed
      const [first, second] = coords;
      
      // If the first value is likely to be latitude (between -90 and 90)
      // and the second is likely to be longitude (beyond that range)
      if (Math.abs(first) <= 90 && Math.abs(second) > 90) {
        return [second, first]; // Swap to ensure longitude comes first
      }
      return coords;
    }
    
    // Handle string format like "lng,lat"
    if (typeof coords === 'string') {
      const parts = coords.split(',').map(c => parseFloat(c.trim()));
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return parts;
      }
    }
    
    // Default to a fallback if we can't parse
    console.error('[MapComponent] Invalid coordinates format:', coords);
    return [0, 0];
  }

  // Mark customers with valid coordinates
  const validCustomers = useMemo(() => {
    const filtered = customers.filter(customer => 
      customer.address && 
      customer.address.coordinates &&
      isValidCoordinate(customer.address.coordinates)
    );
    if (MAP_DEBUG) {
      try {
        const sample = filtered.slice(0, 3).map(c => ({ name: c.name, coords: c.address.coordinates }));
        // eslint-disable-next-line no-console
        MAP_DEBUG && console.log('[MapComponent][Debug] validCustomers:', { count: filtered.length, sample });
      } catch (_) {}
    }
    return filtered;
  }, [customers]);

  // Clear existing markers
  const clearMarkers = () => {
    markersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    markersRef.current = [];
  };
  
  // Add markers when customers data changes
  useEffect(() => {
    MAP_DEBUG && console.log('[MapComponent] useEffect triggered - customers:', customers.length, 'validCustomers:', validCustomers.length);
    MAP_DEBUG && console.log('[MapComponent] Map ready:', !!map.current, 'Loading:', loading, 'Error:', error);
    
    if (!map.current || loading || error) {
      MAP_DEBUG && console.log('[MapComponent] Skipping marker creation - map not ready');
      return;
    }
    
    try {
      // Clear any existing markers
      clearMarkers();
      
      // Check if we have customers with valid coordinates
      if (validCustomers.length > 0) {
        // Create bounds to fit all markers
        const bounds = new window.google.maps.LatLngBounds();
        let markersAdded = 0;
        let skippedCount = 0;
        
        validCustomers.forEach((customer, index) => {
          try {
            // Ensure coordinates are in the correct format and valid
            const coordinates = ensureCorrectFormat(customer.address.coordinates);
            
            if (!isValidCoordinate(coordinates)) {
              skippedCount++;
              return; // Skip this customer
            }
            
            const [lng, lat] = coordinates;
            
            // Create marker position
            const position = { lat: lat, lng: lng };
            
            // Create marker
            const marker = new window.google.maps.Marker({
              position: position,
              map: map.current,
              title: customer.name,
              animation: window.google.maps.Animation.DROP
            });
            if (MAP_DEBUG) {
              // eslint-disable-next-line no-console
              MAP_DEBUG && console.log('[MapComponent][Debug] Marker added', { name: customer.name, position });
            }
            
            // Create popup content
            const popupContent = `
              <div style="font-family: Arial, sans-serif; max-width: 250px;">
                <h3 style="margin: 0 0 10px 0; color: #333;">${customer.name}</h3>
                <p style="margin: 5px 0;">${customer.address.street || ''}</p>
                <p style="margin: 5px 0;">${customer.address.city || ''}, ${customer.address.state || ''} ${customer.address.zip || ''}</p>
                ${customer.phone ? `<p style="margin: 5px 0;"><strong>Phone:</strong> ${customer.phone}</p>` : ''}

              </div>
            `;
            
            // Add click event to marker
            marker.addListener('click', () => {
              // Close any open info window
              if (infoWindowRef.current) {
                infoWindowRef.current.close();
              }
              
              // Set content and open info window
              infoWindowRef.current.setContent(popupContent);
              infoWindowRef.current.open(map.current, marker);
              
              // Call the onMarkerClick callback if provided
              if (onMarkerClick) {
                onMarkerClick(customer);
              }
            });
            
            // Store marker reference
            markersRef.current.push(marker);
            
            // Extend bounds to include this marker
            bounds.extend(position);
            markersAdded++;
          } catch (err) {
            skippedCount++;
            if (process.env.NODE_ENV === 'development') {
              console.error(`[MapComponent] Error adding marker for ${customer.name}:`, err);
            }
          }
        });
        
        // Log summary only
        if (markersAdded > 0 || skippedCount > 0) {
          MAP_DEBUG && console.log(`[MapComponent] ✅ Added ${markersAdded} markers${skippedCount > 0 ? `, skipped ${skippedCount} invalid` : ''}`);
        }
        
        // Fit the map to the bounds with padding
        if (!bounds.isEmpty()) {
          setTimeout(() => {
            map.current.fitBounds(bounds, {
              padding: 70
            });
            
            // Ensure we don't zoom in too much
            const maxZoom = 14;
            if (map.current.getZoom() > maxZoom) {
              map.current.setZoom(maxZoom);
            }
            if (MAP_DEBUG) {
              try {
                const center = map.current.getCenter();
                const debugCenter = center ? { lat: center.lat(), lng: center.lng() } : null;
                // eslint-disable-next-line no-console
                MAP_DEBUG && console.log('[MapComponent][Debug] Bounds fit complete', {
                  markersAdded,
                  finalZoom: map.current.getZoom(),
                  finalCenter: debugCenter
                });
              } catch (_) {}
            }
          }, 100);
        }
      } else if (MAP_DEBUG) {
        // eslint-disable-next-line no-console
        MAP_DEBUG && console.warn('[MapComponent][Debug] No customers with valid coordinates. Using default center/zoom.');
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error updating markers:', err);
      }
      setError('Error displaying customer markers.');
    }
  }, [customers, loading, error, onMarkerClick, validCustomers]);
  
  // Function to retry map initialization
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    
    // Small delay before retry
    setTimeout(() => {
      initializeMap();
    }, 500);
  };
  
  // Handle map type change
  const handleMapTypeChange = (event, newMapType) => {
    if (newMapType !== null) {
      setMapType(newMapType);
    }
  };

  // Error display with enhanced information
  if (error) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          textAlign: 'center', 
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: 2
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Map Error
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Check the browser console for detailed diagnostic information.
        </Typography>
        <Button 
          variant="contained" 
          onClick={handleRetry}
          startIcon={<RefreshIcon />}
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative', height: '400px', width: '100%' }}>
      {/* Map Type Toggle */}
      <Box sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10, 
        zIndex: 1000,
        backgroundColor: 'white',
        borderRadius: 1,
        boxShadow: 1
      }}>
        <ToggleButtonGroup
          value={mapType}
          exclusive
          onChange={handleMapTypeChange}
          size="small"
        >
          <ToggleButton value="roadmap">
            <Tooltip title="Street Map">
              <MapIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="satellite">
            <Tooltip title="Satellite">
              <SatelliteIcon />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="hybrid">
            <Tooltip title="Hybrid">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <MapIcon sx={{ fontSize: 12 }} />
                <SatelliteIcon sx={{ fontSize: 12, ml: 0.5 }} />
              </Box>
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Loading overlay */}
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          zIndex: 1000
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading Google Maps...
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Check console for detailed progress
            </Typography>
          </Box>
        </Box>
      )}

      {/* Map container */}
      <div 
        ref={mapContainer} 
        style={{ 
          height: '100%', 
          width: '100%',
          borderRadius: '8px',
          overflow: 'hidden'
        }} 
      />
    </Box>
  );
}

export default MapComponent; 